import { useCallback, useEffect, useState } from 'react';
import { auditCoherenceTresorerie } from '../lib/dataProvider';
import { fmt, PageTitle } from '../components/shared/badges';

const cellule = 'px-3 py-2 text-left text-sm';
const sens = (v) => v === 'entree' ? 'Encaissement' : 'Décaissement';

export default function CoherenceTresorerie() {
  const [audit, setAudit] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [encours, setEncours] = useState(false);
  const charger = useCallback(async () => {
    setEncours(true); setErreur(null); setAudit(null);
    try { setAudit(await auditCoherenceTresorerie()); }
    catch (e) { setErreur(String(e.message || e)); }
    finally { setEncours(false); }
  }, []);
  useEffect(() => { charger(); }, [charger]);
  return <div data-testid="coherence-tresorerie-page" className="space-y-6">
    <PageTitle titre="Contrôle de cohérence trésorerie" emoji="🔍"
      sous="Rapprochement des bons et des écritures de trésorerie depuis un instantané serveur"
      action={<button data-testid="coherence-relancer" disabled={encours} onClick={charger}
        className="bg-slate-900 px-4 py-2 text-white disabled:opacity-50">{encours ? 'Contrôle…' : 'Relancer le contrôle'}</button>} />
    <div className="border-l-4 border-amber-500 bg-amber-50 p-4 text-sm">
      Ce contrôle est indicatif : il compare des montants par tiers et par sens, uniquement avec des écritures payées.
      Une correspondance ne prouve pas l’affectation à un bon. Il ne remplace ni les justificatifs,
      ni le rapprochement bancaire, ni le comptage de caisse. Aucune anomalie n’est corrigée automatiquement.
    </div>
    {encours && <p data-testid="coherence-chargement" role="status">Lecture du serveur…</p>}
    {erreur && <p data-testid="coherence-erreur" role="alert" className="bg-red-50 p-4 text-red-800">
      Contrôle impossible : {erreur} Aucun verdict de cohérence disponible.
    </p>}
    {audit && <>
      <p className="text-sm text-slate-600">Instantané serveur : {new Date(audit.le).toLocaleString('fr-FR')}.</p>
      {audit.alertes.map((a) => <p key={a} className="text-amber-800">{a}</p>)}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="bg-white p-4">Bons examinés : <strong>{audit.lignes.length}</strong></div>
        <div className="bg-white p-4">Bons sans couverture suffisante : <strong>{audit.orphelins.length}</strong></div>
        <div className="bg-white p-4">Montant sans couverture : <strong>{fmt(audit.totaux.nonGage, 3)} TND</strong></div>
      </div>
      {!audit.lignes.length ? <p>Aucun bon portant un paiement à examiner sur cet instantané.</p> :
        !audit.orphelins.length ? <p className="bg-amber-50 p-3" data-testid="coherence-aucun-orphelin">
          Aucun manque détecté par rapprochement de montants. Affectations à confirmer avec les justificatifs.
        </p> : <p role="alert" className="text-red-700">Des bons déclarés réglés ne sont pas couverts par des écritures du même sens. À vérifier.</p>}
      <section className="overflow-x-auto bg-white">
        <h2 className="p-3 font-bold">Rapprochement par tiers et sens</h2>
        <table className="w-full min-w-[650px]" data-testid="coherence-table-tiers">
          <thead><tr>{['Tiers', 'Sens', 'Bons réglés', 'Trésorerie payée', 'Écart', 'Résultat indicatif'].map((x)=><th key={x} className={cellule}>{x}</th>)}</tr></thead>
          <tbody>{audit.tiers.map((t)=><tr key={t.key}>
            <td className={cellule}>{t.nom}</td><td className={cellule}>{sens(t.sens)}</td>
            <td className={cellule}>{fmt(t.declare,3)}</td><td className={cellule}>{fmt(t.reel,3)}</td>
            <td className={cellule}>{fmt(t.ecart,3)}</td><td className={cellule}>{t.etat === 'manque' ? 'Couverture insuffisante' : t.etat === 'excedent' ? 'Écritures supplémentaires à expliquer' : 'Montants concordants — à justifier'}</td>
          </tr>)}</tbody>
        </table>
      </section>
      <section className="overflow-x-auto bg-white">
        <h2 className="p-3 font-bold">Détail des bons</h2>
        <table className="w-full min-w-[700px]" data-testid="coherence-table-lignes">
          <thead><tr>{['Bon','Tiers','Sens','Déclaré réglé','Non couvert','Rapprochements indicatifs'].map((x)=><th key={x} className={cellule}>{x}</th>)}</tr></thead>
          <tbody>{audit.lignes.map((l)=><tr key={l.id} className={l.gage ? '' : 'bg-red-50'}>
            <td className={cellule}>{l.id}</td><td className={cellule}>{l.tiers}</td><td className={cellule}>{sens(l.sens)}</td>
            <td className={cellule}>{fmt(l.montant,3)}</td><td className={cellule}>{fmt(l.manquant,3)}</td>
            <td className={cellule}>{l.gages.join(' · ') || 'Aucune écriture candidate'}</td>
          </tr>)}</tbody>
        </table>
      </section>
    </>}
  </div>;
}
